/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { ITableBrowserData } from './table-browser.data';
import { ITableBrowserProps } from './table-browser.props.autogenerated';
import { getTableQueryResponseAsync } from './proxy/DataActionExtension.g';
import DataTable, { TableColumn, TableRow } from 'react-data-table-component';
import { QuerySearchComponent } from './components/QuerySearchComponent';
import { IWhereClause } from './proxy/DataServiceEntities.g';

export interface ITableBrowserViewProps extends ITableBrowserProps<ITableBrowserData> {
    table: React.ReactNode | null;
    searchComponent: React.ReactNode;
}

interface ITableBrowserState {
    count: number;
    isError: boolean;
    tableData: any;
    loading: boolean;
    searchState: ISearchState;
}

export interface ISearchState {
    schema: string;
    table: string;
    top: number;
    skip: number;
    where: IWhereClause[];
    orderBy: string;
    isAscending: boolean;
    columns: string[];
    password: string;
}

/**
 *
 * TableBrowser component
 * @extends {React.PureComponent<ITableBrowserProps<ITableBrowserData>>}
 */
class TableBrowser extends React.PureComponent<ITableBrowserProps<ITableBrowserData>, ITableBrowserState> {
    constructor(props: ITableBrowserProps<ITableBrowserData>) {
        super(props);

        const defaultSearchState: ISearchState = {
            table: '',
            schema: '',
            columns: [],
            isAscending: false,
            orderBy: '',
            skip: 0,
            top: 25,
            where: [],
            password: ''
        };

        this.state = {
            count: 0,
            isError: false,
            tableData: null,
            loading: false,
            searchState: defaultSearchState
        };

        this.updateSearchState = this.updateSearchState.bind(this);
        this.getData = this.getData.bind(this);
        this.doSearch = this.doSearch.bind(this);
        this.updateRowsPerPage = this.updateRowsPerPage.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
    }

    private get tableColumns(): TableColumn<any> | null {
        const { tableData } = this.state;

        if (tableData) {
            // @ts-ignore
            return Object.keys(tableData[0]).map(heading => {
                return {
                    name: heading,
                    selector: (row: any) => row[heading],
                    reorder: true
                };
            });
        }

        return null;
    }

    private get tableRows(): TableRow | null {
        const { tableData } = this.state;

        if (tableData) {
            return tableData.map((result: any, i: number) => {
                const row = { id: i };
                Object.keys(result).forEach(key => {
                    row[key] = result[key];
                });
                return row;
            });
        }
        return null;
    }

    private renderSearchComponent(): React.ReactNode {
        return (
            <QuerySearchComponent searchState={this.state.searchState} updateSearchState={this.updateSearchState} search={this.doSearch} />
        );
    }

    private doSearch() {
        void this.getData(this.state.searchState);
    }

    private updateSearchState(searchState: ISearchState) {
        this.setState({ searchState: searchState });
    }

    private updateRowsPerPage(rowsPerPage: number) {
        this.setState({ searchState: { ...this.state.searchState, top: rowsPerPage, skip: 0 } }, () =>
            this.getData(this.state.searchState)
        );
    }

    private handlePageChange(page: number) {
        this.setState({ searchState: { ...this.state.searchState, skip: this.state.searchState.top * (page - 1) } }, () =>
            this.getData(this.state.searchState)
        );
    }

    private renderTable(): React.ReactNode {
        const cols = this.tableColumns;
        const rows = this.tableRows;
        if (cols && rows) {
            // @ts-ignore
            return (
                <DataTable
                    // @ts-ignore
                    columns={cols}
                    // @ts-ignore
                    data={rows}
                    pagination
                    paginationServer
                    paginationTotalRows={this.state.count}
                    onChangeRowsPerPage={this.updateRowsPerPage}
                    onChangePage={this.handlePageChange}
                    paginationPerPage={25}
                />
            );
        }
        return null;
    }

    private async getData(searchState: ISearchState) {
        void getTableQueryResponseAsync(
            { callerContext: this.props.context.actionContext },
            searchState.schema,
            searchState.table,
            searchState.columns,
            [],
            searchState.orderBy,
            searchState.isAscending,
            searchState.top,
            searchState.skip,
            searchState.password
        ).then(data => {
            this.setState({
                tableData: JSON.parse(data?.JsonResults || '')?.Results,
                isError: data.IsError || false,
                count: data.Count || 0
            });
        });
    }

    // public componentDidMount() {
    //     const searchState: ISearchState = {
    //         top: 25,
    //         skip: 0,
    //         table: 'inventTable',
    //         schema: 'ext',
    //         where: [],
    //         orderBy: 'ITEMID',
    //         isAscending: false,
    //         columns: ['*'],
    //         password: 'password'
    //     };
    //     void this.getData(searchState);
    // }

    public render(): JSX.Element | null {
        const viewProps: ITableBrowserViewProps = {
            ...this.props,
            table: this.renderTable(),
            searchComponent: this.renderSearchComponent()
        };

        return this.props.renderView(viewProps);
    }
}

export default TableBrowser;
